import React, { useMemo, useState } from 'react';
import './styles/ResultList.css';
import * as XLSX from 'xlsx';

import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { MultiSelect } from "primereact/multiselect";

import "primereact/resources/themes/lara-light-cyan/theme.css";

const ResultList = ({ results, travelTimes }) => {
    const data = useMemo(() => results || [], [results]);
    const travelData = useMemo(() => travelTimes || [], [travelTimes]);

    const availableModes = useMemo(() => {
        if (travelData.length > 0) {
            return {
                showWalking: 'walking' in travelData[0],
                showDriving: 'driving' in travelData[0],
                showBicycling: 'bicycling' in travelData[0],
                showTransit: 'transit' in travelData[0]
            };
        }
        return {
            showWalking: false,
            showDriving: false,
            showBicycling: false,
            showTransit: false
        };
    }, [travelData]);

    const [selectedColumns, setSelectedColumns] = useState([
        "Codigo", "Provincia", "Localidad", "Domicilio"
    ]);
    
    const allColumns = [
        { field: "Codigo", header: "Código" },
        { field: "ComunidadAutonoma", header: "Comunidad Autonoma" },
        { field: "Provincia", header: "Provincia" },
        { field: "Localidad", header: "Localidad" },
        { field: "Domicilio", header: "Domicilio" }
    ];

    const tableData = useMemo(() => {
        return data.map((center, index) => {
            const row = {
                ComunidadAutonoma: center.ComunidadAutonoma,
                Provincia: center.Provincia,
                Localidad: center.Localidad,
                DenominacionEspecifica: center.DenominacionEspecifica,
                Codigo: center.Codigo,
                Domicilio: center.Domicilio,
            };

            if (availableModes.showWalking) row['walking'] = travelData[index]?.['walking'] || 'N/A';
            if (availableModes.showDriving) row['driving'] = travelData[index]?.['driving'] || 'N/A';
            if (availableModes.showBicycling) row['bicycling'] = travelData[index]?.['bicycling'] || 'N/A';
            if (availableModes.showTransit) row['transit'] = travelData[index]?.['transit'] || 'N/A';

            row.urlMaps = travelData[index]?.urlMaps || null;
            return row;
        });
    }, [data, travelData, availableModes]);

    // Custom cell renderer for the google maps link column
    const linkTemplate = (row) => {
        return (row.urlMaps ? (
            <a href={row.urlMaps} target="_blank" rel="noopener noreferrer">
                Haz click aquí
            </a>
            ) : (
                'No disponible'
            )
        );
    };

    const exportToExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(tableData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Resultados');
        XLSX.writeFile(workbook, 'Resultados.xlsx');
    };

     // Function to parse time into total minutes
     const parseDuration = (time) => {
        if (!time || typeof time !== "string") return 0;
    
        // Match days, hours, and minutes separately
        const daysMatch = time.match(/(\d+)\s*days?/i);
        const hoursMatch = time.match(/(\d+)\s*hours?/i);
        const minutesMatch = time.match(/(\d+)\s*mins?/i);
    
        const days = daysMatch ? parseInt(daysMatch[1], 10) : 0;
        const hours = hoursMatch ? parseInt(hoursMatch[1], 10) : 0;
        const minutes = minutesMatch ? parseInt(minutesMatch[1], 10) : 0;
    
        return days * 1440 + hours * 60 + minutes;
    };

    // Custom sort function for the duration column
    const durationSort = (e) => {
        const { field, order, data } = e;

        // Sort the data array based on the specified field and order
        return data.sort((a, b) => {
            const value1 = parseDuration(a[field] || "0 min");
            const value2 = parseDuration(b[field] || "0 min");
            
            return order * (value1 - value2);
        });
    };

    return (
        <Card className="min-w-460 m-t-20">
            {tableData.length === 0 ? (
                <p>No se encontraron resultados para los códigos ingresados.</p>
            ) : (
                <>
                    <div className="row" >
                        <div className="col-md-10 text-start">
                            <label>Selecciona las columnas :</label> 
                                <MultiSelect
                                    value={selectedColumns}
                                    options={allColumns.map(col => ({ label: col.header, value: col.field }))}
                                    onChange={(e) => setSelectedColumns(e.value)}
                                    placeholder="Select columns"
                                    style={{ fontSize: '14px', marginLeft: '10px' }}
                                />
                        </div>
                        <div className="col-md-2 text-end">
                            <button onClick={exportToExcel} className="button export-btn-excel">
                                    Exportar a Excel
                            </button>
                        </div>
                    </div>

                    <div className="row m-t-10">
                        <div className="col-12">
                            <DataTable 
                                value={tableData}
                                stripedRows       // Enables striped rows
                                paginator rows={5} rowsPerPageOptions={[5, 10, 25, 50]}
                                sortField="driving" 
                                sortOrder={1}  // 1 for ascending order
                                >
                                {allColumns
                                .filter(col => selectedColumns.includes(col.field))
                                .map(col => (
                                    <Column key={col.field} field={col.field} header={col.header} sortable />
                                ))}
                                <Column field="DenominacionEspecifica" header="Centro" sortable></Column>
                                {availableModes.showWalking && <Column field="walking" header="Andando" sortable sortFunction={durationSort}></Column>}
                                {availableModes.showDriving && <Column field="driving" header="En coche" sortable sortFunction={durationSort}></Column>}
                                {availableModes.showBicycling && <Column field="bicycling" header="En bicicleta" sortable sortFunction={durationSort}></Column>}
                                {availableModes.showTransit && <Column field="transit" header="Transporte público" sortable sortFunction={durationSort}></Column>}
                                <Column field="urlMaps" body={linkTemplate} header="Url Google Maps" ></Column>
                            </DataTable>
                        </div>
                    </div>
                </>
            )}
        </Card>
    );
};

export default ResultList;